// mui- imports
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import excel from "assets/images/excelIcon.png";
import csv from "assets/images/csvIcon.png";
import pdf from "assets/images/pdf.png";
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  ArrowBack,
  FilterAlt,
  ViewColumnTwoTone,
  Refresh,
  BarChart,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// import { authorizedGenApiCall, authorizedReportApiCall } from "api/genApiCalls";
// import { apiDataKeyExplorerToolbarData } from "api/ApiDatakeys";
import { Typography, Stack, Grid, Skeleton } from "@mui/material";

import ColumnPicker from "./components/ColumnPicker";
import { useForm } from "react-hook-form";
import FilesMenu from "./components/FilesMenu";
import { downloadFile, openPdf } from "qubCommon/helperMethods/fileHelper";
import Criteria from "./components/Criteria";
import { formatRequestObject } from "qubCommon/helperMethods/formatRequest";
import useToolbarProps from "./useToolbarProps";
import { authorizedReportApiCall } from "api/genApiCalls";

const ToolbarExplorer = forwardRef((props, ref) => {
  const navigate = useNavigate();
  /* Report Files Excel,CSV,PDF States */

  // Objects describing files : {id,title etc}
  const [menuFiles, setMenuFiles] = useState([]);
  // open,setOpen(anchorEl)
  const [filesMenu, setFilesMenu] = useState(null);
  // 3 Menus have the same Component so in order to open the correct one we have to set fileMenuType before openning
  const [fileMenuType, setFileMenuType] = useState(null);

  // Criteria Dirty Check
  const [dirtyValuesList, setDirtyValuesList] = useState(false);
  const [dirtyFieldSearch, setDirtyFieldSearch] = useState(false);

  // Redux variables

  // props
  const {
    excelHandleClick,
    webPageKey,
    title,
    gridRef,
    form,
    rptRequestVars,
    submit,
    toolbarProps,
    children,
  } = props;
  // const { csvFiles, pdfFiles, excelFiles, columns, criteriaRef } = useToolbarProps({ webPageKey }); migrated to parent component
  const { csvFiles, pdfFiles, excelFiles, columns, criteriaRef } = toolbarProps;

  // methods are used for ColumnPicker visibleFields
  const methods = useForm();

  // Criteria and ColumnPicker Popup Refs
  const colPopupRef = useRef();
  const criteriaPopupRef = useRef();

  // GridRefs is an array of refs used for Dynamic Criteria Component
  const gridRefs = useRef([]);

  const handleDirtyCheck = (initRef) => {
    if (initRef) {
      let dirty = false;
      initRef?.current?.critFldsSearch?.forEach((crit) => {
        if (crit.value1 || crit.value2) {
          dirty = "fs";
        }
      });
      initRef?.current?.critValuesList?.forEach((crit) => {
        let dirtyChildren = 0;
        crit?.children?.forEach((child, index) => {
          if (child?.isSelected) {
            dirtyChildren += 1;
          }
          if (
            dirtyChildren &&
            dirtyChildren !== crit?.children?.length &&
            index === crit?.children?.length - 1
          )
            if (dirty === "fs" || dirty === "fsvl") dirty = "fsvl";
            else dirty = "vl";
        });
      });
      if (dirty === "fsvl") {
        setDirtyFieldSearch(true);
        setDirtyValuesList(true);
      } else if (dirty === "fs") {
        setDirtyFieldSearch(true);
        setDirtyValuesList(false);
      } else if (dirty === "vl") {
        setDirtyFieldSearch(false);
        setDirtyValuesList(true);
      } else {
        setDirtyFieldSearch(false);
        setDirtyValuesList(false);
      }
    } else {
      let dirty = false;
      criteriaRef?.current?.critFldsSearch?.forEach((crit) => {
        if (crit.value1 || crit.value2) {
          dirty = "fs";
        }
      });
      criteriaRef?.current?.critValuesList?.forEach((crit) => {
        let dirtyChildren = 0;
        crit?.children?.forEach((child, index) => {
          if (child?.isSelected) {
            dirtyChildren += 1;
          }
          if (
            dirtyChildren &&
            dirtyChildren !== crit?.children?.length &&
            index === crit?.children?.length - 1
          )
            if (dirty === "fs" || dirty === "fsvl") dirty = "fsvl";
            else dirty = "vl";
        });
      });
      if (dirty === "fsvl") {
        setDirtyFieldSearch(true);
        setDirtyValuesList(true);
      } else if (dirty === "fs") {
        setDirtyFieldSearch(true);
        setDirtyValuesList(false);
      } else if (dirty === "vl") {
        setDirtyFieldSearch(false);
        setDirtyValuesList(true);
      } else {
        setDirtyFieldSearch(false);
        setDirtyValuesList(false);
      }
    }
  };

  // set Tab Title
  // useEffect(() => {
  //   handleDirtyCheck(criteriaRef);
  // }, [criteriaRef?.current?.critValuesList]);

  // populating methods based on fetched data
  useEffect(() => {
    if (columns?.length >= 1) {
      columns?.forEach((c) => {
        c?.children?.forEach((child) => {
          methods.setValue(`${child.fldName}`, Boolean(child.isVisible));
        });
      });
    }
  }, [columns, methods]);

  // function used to map data to grid (binding,header,visible) for unbound grids
  const mapHeaders = (flex) => {
    flex.columns.forEach((c) => {
      c.visible = false;
    });
    flex.columns.forEach((c) => {
      c.wordWrap = true;

      columns.forEach((column) => {
        const res = column.children.find((item) => item.fldName === c.binding);
        if (res && methods.getValues(res?.fldName)) {
          c.visible = true;
          // flex.autoSizeColumn(c.index);
        }
        if (res?.fldTitle) {
          c.header = res?.fldTitle;
        }
      });
    });
  };

  // imperative handler, our Ref comes from our Components Hook. Px. ParastatikaExplorer(Component), useParastatikaExplorer(MainHook): ref is created in useParastatikaExplorer
  useImperativeHandle(ref, () => ({
    mapHeaders,
    criteriaRef,
  }));

  // columnPickerPopUP initialization
  const colPopupInitialized = (popup) => {
    popup.isDraggable = true;
    popup.isResizable = false;
    popup.fadeOut = false;
    popup.fadeIn = false;

    colPopupRef.current = popup;
  };

  // criteriaPopUP initialization
  const criteriaPopupInitialized = (popup) => {
    popup.isDraggable = true;
    popup.isResizable = true;
    popup.fadeOut = false;
    popup.fadeIn = false;
    criteriaPopupRef.current = popup;
  };

  // Pick File, this function will be probided to FilesMenu
  const handleFilePick = async (file) => {
    const filterParams = form.getValues();
    if (file?.rptId === "default") excelHandleClick();
    else if (fileMenuType === "Pdf") {
      // let params;
      // const formValues = formatRequestObject(form?.getValues());
      // if (rptRequestVars && form?.getValues()) {
      //     params = [...rptRequestVars, ...formValues, { dataKey: 'rptId', dataValue: file?.rptId }];
      // } else if (rptRequestVars && !form?.getValues()) {
      //     params = [...rptRequestVars, { dataKey: 'rptId', dataValue: file?.rptId }];
      // } else if (!rptRequestVars && form.getValues()) {
      //     params = [...formValues, { dataKey: 'rptId', dataValue: file?.rptId }];
      // } else params = [{ dataKey: 'rptId', dataValue: file?.rptId }];

      // filterParams.critFldsSearch = formatCritFldSearch(
      //   criteriaRef?.current?.critFldsSearch
      // );
      // filterParams.critValuesList = formatValuesListSearch(
      //   criteriaRef?.current?.critValuesList
      // );

      try {
        const res = await authorizedReportApiCall(
          {
            ReportId: file?.rptId,
            DBKey: "EKE",
            ReportParams: filterParams,
          },
          {
            responseType: "blob",
          }
        );
        openPdf(res);
      } catch {
        console.log("error on get QubReport");
      }
    } else if (fileMenuType === "Excel") {
      try {
        const res = await authorizedReportApiCall(
          {
            ReportId: file?.rptId,
            DBKey: "EKE",
            ReportParams: filterParams,
          },
          {
            responseType: "blob",
          }
        );
        downloadFile(res);
      } catch {
        console.log("error on getQubReport");
      }
    } else if (fileMenuType === "Csv") {
      try {
        const res = await authorizedReportApiCall(
          {
            ReportId: file?.rptId,
            DBKey: "EKE",
            ReportParams: filterParams,
          },
          {
            responseType: "blob",
          }
        );
        downloadFile(res);
      } catch {
        console.log("error on getQubReport");
      }
    }
    setFilesMenu(null);
  };

  // Open Menu based on Icon (xls,csv,pdf)
  const handleMenuOpen = (event, fileType) => {
    switch (fileType) {
      case "xls":
        if (excelFiles?.length > 0) {
          setMenuFiles(excelFiles);
          setFileMenuType("Excel");
          setFilesMenu(event.currentTarget);
        }
        break;
      case "csv":
        if (csvFiles?.length > 0) {
          setMenuFiles(csvFiles);
          setFileMenuType("Csv");
          setFilesMenu(event.currentTarget);
        }
        break;
      case "pdf":
        if (pdfFiles?.length > 0) {
          setMenuFiles(pdfFiles);
          setFileMenuType("Pdf");
          setFilesMenu(event.currentTarget);
        }
        break;
      default:
        console.log("provide a valid file type");
    }
  };

  const handleFilterSubmit = () => {
    submit();
  };

  return (
    <>
      <FilesMenu
        anchorEl={filesMenu}
        setAnchorEl={setFilesMenu}
        files={menuFiles}
        fileMenuType={fileMenuType}
        handleFilePick={handleFilePick}
      />

      <ColumnPicker
        columns={columns}
        popupInitialized={colPopupInitialized}
        gridRef={gridRef}
        methods={methods}
        webPageKey={webPageKey}
      />
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        {/* <Criteria
          popupInitialized={criteriaPopupInitialized}
          methods={methods}
          handleDirtyCheck={handleDirtyCheck}
          dirtyFieldSearch={dirtyFieldSearch}
          dirtyValuesList={dirtyValuesList}
          gridRefs={gridRefs}
          criteriaRef={criteriaRef}
          submit={handleFilterSubmit}
        /> */}

        <Box my={1}>
          <Stack direction="row" spacing={1}>
            <Box boxShadow={2} sx={{ borderRadius: 50 }}>
              <Tooltip title="Πίσω">
                <IconButton onClick={() => navigate(-1)}>
                  <ArrowBack />
                </IconButton>
              </Tooltip>
            </Box>
            {/* <Box
              boxShadow={2}
              sx={{
                borderRadius: 50,
                backgroundColor:
                  dirtyFieldSearch || dirtyValuesList ? "red" : " ",
              }}
            >
              <Tooltip
                title="Φίλτρα"
                onClick={() => criteriaPopupRef.current.show(true)}
                sx={{}}
              >
                <IconButton>
                  <FilterAlt />
                </IconButton>
              </Tooltip>
            </Box> */}
            {columns?.length > 0 && (
              <Box boxShadow={2} sx={{ borderRadius: 50 }}>
                <Tooltip title="Στήλες">
                  <IconButton onClick={() => colPopupRef.current.show(true)}>
                    <ViewColumnTwoTone />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {/* <Box boxShadow={2} sx={{ borderRadius: 50 }}>
              <Tooltip title="Ανανέωση">
                <IconButton>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box> */}
            {/* <Tooltip title="Στατιστικά">
                        <IconButton>
                            <BarChart />
                        </IconButton>
                    </Tooltip> */}
            {/* <span disabled={csvFiles?.length === 0}>
                            <Box boxShadow={2} sx={{ borderRadius: 50 }}>
                                <Tooltip title="Export Csv">
                                    <IconButton onClick={(e) => handleMenuOpen(e, 'csv')}>
                                        <img
                                            src={csv}
                                            style={csvFiles?.length === 0 ? { opacity: 0.4, height: '24px' } : { height: '24px' }}
                                            alt="csv"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </span> */}
            <Box boxShadow={2} sx={{ borderRadius: 50 }}>
              <Tooltip title="Export Excel">
                <IconButton onClick={(e) => handleMenuOpen(e, "xls")}>
                  <img
                    src={excel}
                    alt="excel"
                    style={
                      excelFiles?.length === 0
                        ? { opacity: 0.4, height: "24px" }
                        : { height: "24px" }
                    }
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <span hidden={pdfFiles?.length === 0}>
              <Box boxShadow={2} sx={{ borderRadius: 50 }}>
                <Tooltip title="Export PDF">
                  <IconButton onClick={(e) => handleMenuOpen(e, "pdf")}>
                    <img
                      style={
                        pdfFiles?.length === 0
                          ? { opacity: 0.4, height: "24px" }
                          : { height: "24px" }
                      }
                      src={pdf}
                      alt="excel"
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </span>
            {children}
          </Stack>
        </Box>

        {/* <Box>
                    <Typography variant="h5">{title ?? webPageKey}</Typography>
                </Box> */}
        <Typography variant="h5" sx={{ padding: 1 }}>
          {title}
        </Typography>
      </Box>
    </>
  );
});

export default ToolbarExplorer;
